import React, { useMemo } from 'react';
import { ChartClickInteraction } from '@components/Widgets/types';
import { isHistoricalQuery } from '../../CustomDashboardsUtils';
import {
  CommonWidgetProps,
  ScoreTypeWidget,
  TileCategoryWidgetTypes,
  TileSubType,
  TileTypeWidget,
  TileWidgetVisualization,
  TrendTypeWidget,
} from '../../types';
import NoneTileWidget from './TileWidget/NoneTileWidget';
import ProgressTileWidget from './TileWidget/ProgressTileWidget';
import ScoreTileWidget from './TileWidget/ScoreTileWidget';
import TrendTileWidget from './TileWidget/TrendTileWidget';

interface TileWidgetProps extends CommonWidgetProps {
  widget: TileCategoryWidgetTypes;
  setWidget?: any;
  data?: Record<string, any>[];
  isLoading?: boolean;
  clickInteractions?: ChartClickInteraction[];
}
type BreakdownMetricData = { percentage: number; numerator: number; denominator: number };
const isBreakdownMetric = (obj: any): obj is BreakdownMetricData =>
  typeof obj === 'object' && obj !== null && 'percentage' in obj && 'numerator' in obj && 'denominator' in obj;

const TileWidget: React.FC<TileWidgetProps> = ({ widget, data = [], isLoading, clickInteractions, selected }) => {
  const request = widget.requests[0];
  const metricObj = request.select.metrics[0];
  const metricName = metricObj.alias || metricObj.name;
  const dataObj: number | BreakdownMetricData = data?.[0]?.[metricName];
  const renderTileWidget = useMemo(
    () => ({
      [TileSubType.Tile]: () => {
        const tileWidget = widget as TileTypeWidget;
        const { visualization } = tileWidget.definition.custom;
        return visualization === TileWidgetVisualization.Progress && isBreakdownMetric(dataObj) ? (
          <ProgressTileWidget
            widget={widget as TileTypeWidget}
            metricsValues={[dataObj.percentage, dataObj.numerator, dataObj.denominator]}
            clickInteractions={clickInteractions}
            selected={selected}
          />
        ) : (
          <NoneTileWidget widget={widget} data={data} clickInteractions={clickInteractions} />
        );
      },
      [TileSubType.Score]: () => <ScoreTileWidget widget={widget as ScoreTypeWidget} data={data} isLoading={isLoading} />,
      [TileSubType.Trend]: () =>
        isHistoricalQuery(request) && (
          <TrendTileWidget data={data} metric={metricName} widget={widget as TrendTypeWidget} clickInteractions={clickInteractions} />
        ),
    }),
    [data, widget, selected]
  );
  return renderTileWidget[widget.type]();
};

export default TileWidget;
